import { useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { GetSubsetsResponseDto } from '../../../../../../../backend/src/matter/subset/dto/get-subsets.response.dto';
import { Subset } from '../../../../../api/types/backend-dto-types';
import { KimMode } from '../../../../../types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface AskKimProps {
  defaultSubsetId: string;
  subsets?: GetSubsetsResponseDto[] | Subset[];
  hideSubsetSelector?: boolean;
}

const DEFAULT_EXAMPLE_QUESTIONS = [
  { prompt: 'What is this case about?', type: KimMode.QuestionFlow },
  { prompt: 'Who are the key people in this case?', type: KimMode.QuestionFlow },
];

export function AskKim({ defaultSubsetId, subsets, hideSubsetSelector = false }: AskKimProps) {
  const { matterId } = useParams();
  if (!matterId) return null;

  const navigate = useNavigate();
  const [kimQuestion, setKimQuestion] = useState('');
  const [selectedSubsetId, setSelectedSubsetId] = useState(defaultSubsetId);

  // Determine active subsetId - fixed takes precedence
  const [isFocused, setIsFocused] = useState(false);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get example questions from the selected subset or currentSubset
  const exampleQuestions = useMemo(() => {
    // Use currentSubset if provided (for fixed subset mode), otherwise find from subsets array
    const selectedSubset = subsets?.find((subset) => subset.id === selectedSubsetId);
    const rawQuestions =
      selectedSubset?.exampleKimQuestions?.question?.map((q) => {
        return { prompt: q.prompt, type: KimMode.QuestionFlow };
      }) ?? [];
    const rawDrafts =
      selectedSubset?.exampleKimQuestions?.draft?.map((q) => {
        return { prompt: q.prompt, type: KimMode.DraftFlow };
      }) ?? [];
    const questions = [...rawQuestions, ...rawDrafts];
    return questions.length > 0 ? questions : DEFAULT_EXAMPLE_QUESTIONS;
  }, [subsets, selectedSubsetId]);

  const handleAskKim = (question: string, type: KimMode) => {
    if (!matterId || !question || !question.trim()) return;

    trackCustomEvent(
      hideSubsetSelector ? TrackingEventsV2.AskKimFromSubsetSummary : TrackingEventsV2.AskKimFromMatterOverview,
      { matterId, subsetId: selectedSubsetId },
    );

    navigate(`/matters/${matterId}/subsets/${selectedSubsetId}/kim`, {
      state: { question, mode: type },
    });
  };

  const handleExampleClick = (question: string, type: KimMode) => {
    handleAskKim(question, type);
    setIsFocused(true);
  };

  // Reset question index when selected subset changes
  useEffect(() => {
    setCurrentQuestionIndex(0);
  }, [selectedSubsetId]);

  // Animated typing placeholder effect
  useEffect(() => {
    // Don't animate if input has value or is focused
    if (kimQuestion.trim() || isFocused) {
      setAnimatedPlaceholder('');
      setIsTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      return;
    }

    // Ensure we have questions and valid index
    if (!exampleQuestions || exampleQuestions.length === 0) {
      return;
    }

    const currentQuestion = exampleQuestions[currentQuestionIndex].prompt;
    if (!currentQuestion) {
      return;
    }

    let currentCharIndex = 0;
    setIsTyping(true);

    const typeNextChar = () => {
      if (currentCharIndex <= currentQuestion.length) {
        setAnimatedPlaceholder(currentQuestion.slice(0, currentCharIndex));
        currentCharIndex++;
        typingTimeoutRef.current = setTimeout(typeNextChar, 30); // 50ms per character
      } else {
        // Finished typing, wait before erasing
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          setAnimatedPlaceholder('');
          // Move to next question
          setCurrentQuestionIndex((prev) => (prev + 1) % exampleQuestions.length);
        }, 3000); // Show completed question for 3 seconds
      }
    };

    // Start typing after a short delay
    typingTimeoutRef.current = setTimeout(typeNextChar, 500);

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [kimQuestion, isFocused, currentQuestionIndex, exampleQuestions]);

  // Filter to only show ready subsets (not processing or failed)
  const readySubsets = subsets?.filter((subset) => subset.status === 'ready') ?? [];
  const showSuggestions = !kimQuestion.trim() && !isFocused;

  return (
    <div
      className={`flex w-full flex-col overflow-hidden rounded-lg border bg-white transition-all duration-300 ${
        isFocused ? 'border-teal-400/40 bg-gray-50/30 shadow-sm' : 'border-gray-200 bg-gray-50/20 shadow-sm'
      }`}
      style={{
        boxShadow: '0 0 20px 2px rgba(20, 184, 166, 0.15)',
      }}
    >
      <div className="flex items-center justify-between gap-3 rounded-t-lg border-b bg-gray-50 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Icon className="lucide--bot h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-700">Ask Kim</span>
        </div>
        <div className="flex items-end justify-between gap-2">
          {!hideSubsetSelector && (
            <div className="flex items-center gap-2 rounded-md border bg-white px-2 py-1">
              <p className="text-xs font-semibold text-gray-500">Search in:</p>
              <Select value={selectedSubsetId} onValueChange={setSelectedSubsetId}>
                <SelectTrigger className="h-auto w-auto gap-1.5 border-0 p-0 text-xs text-gray-500 italic hover:text-gray-700 focus:ring-0">
                  <SelectValue placeholder="Select subset" />
                </SelectTrigger>
                <SelectContent>
                  {readySubsets.map((subset) => (
                    <SelectItem key={subset.id} value={subset.id}>
                      <span className="flex items-center gap-2 text-xs not-italic">
                        <Icon className={subset.isAllDocuments ? 'lucide--files' : 'lucide--group'} />
                        {subset.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 px-4 pt-4 pb-2">
        <div className="items center relative flex justify-between">
          <input
            className="max-h-24 w-full resize-none bg-transparent text-sm outline-none placeholder:text-gray-400"
            value={kimQuestion}
            onChange={(e) => setKimQuestion(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAskKim(kimQuestion, KimMode.QuestionFlow);
              }
            }}
          />
          {!kimQuestion.trim() && !isFocused && animatedPlaceholder && (
            <div className="pointer-events-none absolute top-0 left-0 flex items-center text-sm text-gray-400">
              <span>{animatedPlaceholder}</span>
              {isTyping && <span className="ml-0.5 animate-pulse">|</span>}
            </div>
          )}
          <Button
            variant="ghost"
            disabled={!kimQuestion.trim()}
            className="bg-brandSecondary hover:bg-brandSecondary-hover flex h-7 w-7 cursor-pointer items-center justify-center rounded-full p-1 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => handleAskKim(kimQuestion, KimMode.QuestionFlow)}
          >
            <Icon className="lucide--arrow-right h-5 w-5 text-white" />
          </Button>
        </div>

        <div
          className={`grid transition-all duration-300 ease-in-out ${
            showSuggestions ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="flex h-7 min-w-0 items-center gap-1.5 overflow-x-auto overflow-y-hidden py-6 text-xs text-ellipsis whitespace-nowrap text-gray-600 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent">
              <span className="shrink-0 font-medium">Suggested questions:</span>
              {exampleQuestions.map((question, index) => (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleExampleClick(question.prompt, question.type)}
                      className="max-w-[300px] shrink-0 truncate rounded-md border border-gray-200 bg-gray-100 px-2 py-1 text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-200 hover:text-gray-700"
                    >
                      {question.prompt}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm bg-gray-50 text-center">
                    <p className="whitespace-normal">{question.prompt}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
